import { useState, useRef, useEffect } from 'react';
import type { ReactNode, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import type { Label, Priority, Project, TaskRecurrence } from '@/data/types';
import { ProjectSelect } from '@/components/task/ProjectSelect';
import { LabelsSelect } from '@/components/task/LabelsSelect';
import { AssigneeSelect } from '@/components/task/AssigneeSelect';
import { PrioritySelect } from '@/components/task/PrioritySelect';
import { RecurrenceField } from '@/components/task/RecurrenceField';
import Markdown from '@/components/LazyMarkdown';

export interface TaskFieldsValue {
  title: string;
  description: string;
  project_id: string;
  priority: Priority | null;
  assignee_id: string | null;
  due_date: string | null;
  recurrence: TaskRecurrence | null;
  labelIds: string[];
}

interface FieldUser {
  id: string;
  username: string;
  color: string;
}

function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <p className="text-[12px] font-semibold tracking-wide uppercase text-faint mb-1.5">
      {children}
    </p>
  );
}

/** Formulario de campos de tarea compartido por el modal de creación y las
 *  pestañas Tarea y Detalles (paridad de campos y layout). Presentacional:
 *  el padre decide cómo persiste cada cambio.
 *
 *  Las secciones son independientes: el modal de creación las muestra todas;
 *  la pestaña Tarea solo texto (título + descripción) y la pestaña Detalles
 *  solo las filas de campos.
 *
 *  Orden de filas: etapa | prioridad | asignado · proyecto | etiquetas ·
 *  vencimiento | subtareas | repite. */
export function TaskFields({
  value,
  onChange,
  onTextCommit,
  titleError,
  idPrefix,
  projects,
  labels,
  users,
  titleInputRef,
  titleExtra,
  stageSlot,
  subtasksSlot,
  showTitle = true,
  showFieldRows = true,
  showDescription = true,
  children,
}: {
  value: TaskFieldsValue;
  onChange: (patch: Partial<TaskFieldsValue>) => void;
  onTextCommit?: (field: 'title' | 'description') => void;
  titleError?: string | null;
  idPrefix: string;
  projects: Project[];
  labels: Label[];
  users: FieldUser[];
  titleInputRef?: Ref<HTMLInputElement>;
  titleExtra?: ReactNode;
  stageSlot?: ReactNode;
  subtasksSlot?: ReactNode;
  showTitle?: boolean;
  showFieldRows?: boolean;
  showDescription?: boolean;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const [descFocused, setDescFocused] = useState(false);
  const descRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (descFocused && descRef.current) {
      descRef.current.focus();
      const len = descRef.current.value.length;
      descRef.current.setSelectionRange(len, len);
    }
  }, [descFocused]);

  /* Auto-crece con el contenido mientras se escribe (tope: 55 % del viewport) */
  useEffect(() => {
    const el = descRef.current;
    if (!el || !descFocused) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value.description, descFocused]);

  const showPreview = !descFocused && value.description.trim() !== '';

  return (
    <div className="space-y-5">
      {showTitle && (
        <div>
          <label
            htmlFor={`${idPrefix}-title`}
            className="block text-[12px] font-semibold tracking-wide uppercase text-faint mb-1.5"
          >
            {t('task.titleLabel')}
          </label>
          <input
            ref={titleInputRef}
            id={`${idPrefix}-title`}
            type="text"
            value={value.title}
            maxLength={200}
            placeholder={t('task.titlePlaceholder')}
            onChange={(e) => onChange({ title: e.target.value })}
            onBlur={() => onTextCommit?.('title')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            aria-invalid={titleError !== null && titleError !== undefined}
            className="w-full bg-surface2 border border-app rounded-xl px-3.5 py-2.5 text-[15px] font-medium outline-none focus:border-brand"
          />
          {titleError && (
            <p role="alert" className="text-[12px] text-rose-600 dark:text-rose-400 mt-1">
              {titleError}
            </p>
          )}
          {titleExtra}
        </div>
      )}

      {/* Fila 1: Etapa | Prioridad | Asignado */}
      {showFieldRows && (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-5">
        <div>
          <FieldLabel>{t('task.stage')}</FieldLabel>
          {stageSlot}
        </div>

        <div>
          <FieldLabel>{t('task.priority')}</FieldLabel>
          <PrioritySelect
            id={`${idPrefix}-priority`}
            value={value.priority}
            onChange={(pr) => onChange({ priority: pr })}
            ariaLabel={t('task.priority')}
          />
        </div>

        <div>
          <FieldLabel>{t('task.assignee')}</FieldLabel>
          <AssigneeSelect
            id={`${idPrefix}-assignee`}
            users={users}
            value={value.assignee_id}
            onChange={(userId) => onChange({ assignee_id: userId })}
            ariaLabel={t('task.assignee')}
          />
        </div>
      </div>
      )}

      {/* Fila 2: Proyecto | Etiquetas */}
      {showFieldRows && (
      <div className="grid grid-cols-2 gap-x-4 gap-y-5">
        <div>
          <FieldLabel>{t('task.project')}</FieldLabel>
          <ProjectSelect
            id={`${idPrefix}-project`}
            value={value.project_id}
            onChange={(projectId) => onChange({ project_id: projectId })}
            projects={projects}
            ariaLabel={t('task.project')}
          />
        </div>

        <div>
          <FieldLabel>{t('task.labels')}</FieldLabel>
          <LabelsSelect
            id={`${idPrefix}-labels`}
            labels={labels}
            selected={value.labelIds}
            onChange={(ids) => onChange({ labelIds: ids })}
            ariaLabel={t('task.labels')}
          />
        </div>
      </div>
      )}

      {/* Fila 3: Vencimiento | Subtareas | Repite */}
      {showFieldRows && (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-5">
        <div>
          <FieldLabel>{t('task.dueDate')}</FieldLabel>
          <input
            id={`${idPrefix}-due`}
            type="date"
            value={value.due_date ?? ''}
            onChange={(e) => onChange({ due_date: e.target.value || null })}
            className="w-full bg-surface2 border border-app rounded-xl px-3 py-2.5 text-[14px] outline-none focus:border-brand"
          />
        </div>

        <div>
          <FieldLabel>{t('task.subtasks')}</FieldLabel>
          {subtasksSlot}
        </div>

        <div>
          <RecurrenceField
            value={value.recurrence}
            onChange={(r) => onChange({ recurrence: r })}
            idPrefix={idPrefix}
          />
        </div>
      </div>
      )}

      {showDescription && (
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label
            htmlFor={`${idPrefix}-description`}
            className="text-[12px] font-semibold tracking-wide uppercase text-faint"
          >
            {t('task.description')}
          </label>
          <span className="text-[11px] text-faint">{t('task.markdownHint')}</span>
        </div>
        <div className="relative">
          <textarea
            ref={descRef}
            id={`${idPrefix}-description`}
            value={value.description}
            maxLength={5000}
            rows={4}
            placeholder={t('task.descriptionPlaceholder')}
            onChange={(e) => onChange({ description: e.target.value })}
            onFocus={() => setDescFocused(true)}
            onBlur={() => {
              setDescFocused(false);
              onTextCommit?.('description');
            }}
            style={{ maxHeight: '55vh' }}
            className={`w-full bg-surface2 border border-app rounded-xl px-3.5 py-2.5 text-[15px] leading-relaxed outline-none focus:border-brand overflow-y-auto resize-none ${showPreview ? 'sr-only' : ''}`}
          />
          {showPreview && (
            <div
              onClick={() => setDescFocused(true)}
              className="w-full rounded-xl border border-app bg-surface px-3.5 py-2.5 cursor-text"
              role="button"
              tabIndex={0}
              aria-label={t('task.editDescription')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setDescFocused(true);
                }
              }}
            >
              <Markdown>{value.description}</Markdown>
            </div>
          )}
        </div>
      </div>
      )}

      {children}
    </div>
  );
}
